import React, { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import loupeModel from "./assets/Loupe.glb";
import "./Loupe3DView.css";
import BR from "./assets/BR.glb";

const axisConfig = [
  { label: "X", axis: "x", min: -0.6, max: 0.6, step: 0.02, default: 0 },
  { label: "Y", axis: "y", min: -0.6, max: 0.6, step: 0.02, default: -0.05 },
  { label: "Z", axis: "z", min: -0.9, max: 0.9, step: 0.02, default: 0 },
];

const INITIAL_ROTATION_X = -Math.PI / 2;
const INITIAL_ROTATION_Y = Math.PI / 6;

const Loupe3DView = () => {
  const [movement, setMovement] = useState("center");
  const [axisOffsets, setAxisOffsets] = useState(
    axisConfig.reduce((acc, cfg) => {
      acc[cfg.axis] = cfg.default;
      return acc;
    }, {})
  );
  const canvasRef = useRef(null);
  const modelRef = useRef(null);

  useEffect(() => {
    const updateMovement = () => {
      const section2 = document.getElementById("section-2");
      const section3 = document.getElementById("section-3");
      const triggerPoint = window.scrollY + window.innerHeight / 2;

      if (section3 && triggerPoint >= section3.offsetTop) {
        setMovement("right");
      } else if (section2 && triggerPoint >= section2.offsetTop) {
        setMovement("left");
      } else {
        setMovement("center");
      }
    };

    updateMovement();
    window.addEventListener("scroll", updateMovement, { passive: true });
    return () => window.removeEventListener("scroll", updateMovement);
  }, []);

  useEffect(() => {
    const canvasHolder = canvasRef.current;
    if (!canvasHolder) {
      return undefined;
    }

    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
    });
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.outputColorSpace = THREE.SRGBColorSpace;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(45, 1, 0.1, 100);
    camera.position.set(0, 0.8, 2.6);

    const ambientLight = new THREE.AmbientLight(0xffffff, 0.8);
    scene.add(ambientLight);
    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.6);
    directionalLight.position.set(5, 10, 5);
    scene.add(directionalLight);

    const loader = new GLTFLoader();
    loader.load(
      BR,
      (gltf) => {
        modelRef.current = gltf.scene;
        modelRef.current.rotation.x = INITIAL_ROTATION_X;
        modelRef.current.rotation.y = INITIAL_ROTATION_Y;
        scene.add(modelRef.current);
        Object.entries(axisOffsets).forEach(([axis, value]) => {
          modelRef.current.position[axis] = value;
        });
      },
      undefined,
      (error) => {
        console.error("Erreur de chargement du modèle 3D :", error);
      }
    );

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.enableZoom = true;
    controls.autoRotate = false;
    controls.enablePan = false;
    controls.minDistance = 1.5;
    controls.maxDistance = 4;

    const resize = () => {
      const { width, height } = canvasHolder.getBoundingClientRect();
      const validWidth = width || 360;
      const validHeight = height || 320;
      renderer.setSize(validWidth, validHeight, false);
      camera.aspect = validWidth / validHeight;
      camera.updateProjectionMatrix();
    };

    resize();
    canvasHolder.appendChild(renderer.domElement);
    window.addEventListener("resize", resize);

    let animationFrameId;
    const animate = () => {
      animationFrameId = requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };

    animate();

    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener("resize", resize);
      controls.dispose();
      renderer.dispose();
      scene.clear();
      if (canvasHolder.contains(renderer.domElement)) {
        canvasHolder.removeChild(renderer.domElement);
      }
    };
  }, []);

  useEffect(() => {
    if (modelRef.current) {
      Object.entries(axisOffsets).forEach(([axis, value]) => {
        modelRef.current.position[axis] = value;
      });
    }
  }, [axisOffsets]);

  const movementClass = {
    center: "",
    left: "loupe-3d--left",
    right: "loupe-3d--right",
  }[movement];

  const pivotClass = movement === "left" ? "loupe-3d--pivot" : "";
  const wrapperClass = ["loupe-3d", movementClass, pivotClass]
    .filter(Boolean)
    .join(" ");

  const handleAxisChange = (axis, value) => {
    setAxisOffsets((prev) => ({ ...prev, [axis]: value }));
  };

  return (
    <section className={wrapperClass}>
      <div className="loupe-3d__card">
        <header className="loupe-3d__header">
          <p className="loupe-3d__eyebrow">Exploration interactive</p>
          <h2 className="loupe-3d__title">Loupe en 3D</h2>
          <p className="loupe-3d__subtitle">
            Déplacez-vous autour de la loupe, zoom et rotation sont disponibles
            via la souris ou le doigt.
          </p>
        </header>
        <div ref={canvasRef} className="loupe-3d__canvas" />
        <div className="loupe-3d__axis-controls">
          {axisConfig.map(({ label, axis, min, max, step }) => (
            <label className="loupe-3d__axis-control" key={axis}>
              <span>{label}</span>
              <input
                type="range"
                min={min}
                max={max}
                step={step}
                value={axisOffsets[axis]}
                aria-label={`Position ${axis}`}
                onChange={(event) =>
                  handleAxisChange(axis, Number(event.target.value))
                }
              />
            </label>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Loupe3DView;
